import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  User,
  Settings,
  BarChart3,
  ShieldCheck,
  Shield,
  Database,
  Mail,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Company",
    desc: "Mid-sized automobile tier 2 component supplier producing stamped, welded and assembled parts for tier 1 and OEM customers.",
  },
  {
    icon: Users,
    title: "Focus Area",
    desc: "Three High-frequency stations with repetitive manual work.",
  },
  {
    icon: Clock,
    title: "Initial Gap",
    desc: "Cell efficiency 4-6 points below target.",
  },
  {
    icon: Clock,
    title: "Pilot Results",
    subtitle: "(after 9-12 months)",
    desc: "Manual handling time -65% to -72%, cell efficiency +3 to +5 points, handling-related defects -40% to -65%.",
  },
];

const DECISION_MAKERS = [
  {
    icon: User,
    badgeBg: "bg-[#F0F7FF]",
    badgeColor: "text-[#0284C7]",
    borderColor: "border-[#E2E8F0]",
    title: "Plant Head",
    desc: "Cell efficiency and cost per part improved. On-time delivery pressure eased.",
  },
  {
    icon: Settings,
    badgeBg: "bg-[#FFF1F2]",
    badgeColor: "text-[#E11D48]",
    borderColor: "border-[#FFE4E6]",
    title: "VP / Head of Manufacturing",
    desc: "Cell efficiency rose 3–5 points. Utilisation visible in real time. Labour cost per unit moved in the right direction.",
  },
  {
    icon: BarChart3,
    badgeBg: "bg-[#F0FDF4]",
    badgeColor: "text-[#16A34A]",
    borderColor: "border-[#DCFCE7]",
    title: "Industrial Engineering",
    desc: "Manual handling time fell 65-72%. Cycle time improved on existing fixtures; no major layout change was required.",
  },
  {
    icon: ShieldCheck,
    badgeBg: "bg-[#FFFBEB]",
    badgeColor: "text-[#D97706]",
    borderColor: "border-[#FEF3C7]",
    title: "Quality Head",
    desc: "Handling-related defects fell 40–65%. Process and traceability records available for customer and internal audits.",
  },
  {
    icon: Shield,
    badgeBg: "bg-[#FAF5FF]",
    badgeColor: "text-[#9333EA]",
    borderColor: "border-[#F3E8FF]",
    title: "EHS / Safety Head",
    desc: "Formal risk assessment completed before power-on. Ergonomic load reduced, compliance pack ready for audit.",
  },
  {
    icon: Database,
    badgeBg: "bg-[#F0F9FF]",
    badgeColor: "text-[#0284C7]",
    borderColor: "border-[#E0F2FE]",
    title: "IT / Digital Transformation",
    desc: "Cell integrated with existing ERP and MES. No separate system of record. Role-based access and a clear security model.",
  },
];

const AutomobileCaseStudy = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/labs#insights");
    }
  };

  // Scroll tracking across the sticky track matching Financial Services Case Study
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  /*
    Animation Timeline matching Financial Services Case Study:
    0.0 -> 0.22: Overlay enters from bottom (100% -> 0%), opacity (0 -> 1)
    0.22 -> 0.75: Overlay stays PINNED over Hero section (0% -> 0%, opacity 1)
    0.75 -> 0.98: Overlay slides UP out of view (0% -> -100%), opacity (1 -> 0)
    1.0+: Exits smoothly
  */
  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar matching exact Screenshot */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button matching exact Screenshot */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Case Study / Automobile Tier 2 Supplier
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Insights</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* Scroll Pinning Container (height allows smooth entry, pin duration, and exit) */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* 1. Underlying Hero Section Image */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/automobile-hero.jpg"
                alt="Automobile Tier 2 Supplier Case Study - Robotic Factory Automation"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* 2. Frosted Blur Overlay Section matching user screenshot */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint & Backdrop Blur */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(13, 23, 38, 0.78)",
                  backdropFilter: "blur(24px)",
                  WebkitBackdropFilter: "blur(24px)",
                }}
              />

              {/* Foreground Content matching Screenshot */}
              <div className="relative z-10 max-w-[1360px] w-full px-4 flex flex-col items-center pt-4 sm:pt-0">
                {/* Eyebrow */}
                <p className="text-[12px] sm:text-[13px] font-bold tracking-[1.5px] sm:tracking-[2px] text-[#38BDF8] select-none">
                  Automobile Tier 2 Supplier
                </p>

                {/* Main Heading with Highlighted Colors matching user screenshot (Exactly 2 Lines) */}
                <h1 className="mt-5 sm:mt-6 text-[26px] sm:text-[34px] md:text-[42px] lg:text-[46px] xl:text-[50px] font-extrabold leading-[1.2] sm:leading-[1.16] text-white tracking-tight">
                  <span className="block whitespace-normal sm:whitespace-nowrap">
                    <span className="text-white">Cutting manual </span>
                    <span className="text-[#3882F6]">handling time and improving</span>
                  </span>
                  <span className="block whitespace-normal sm:whitespace-nowrap">
                    <span className="text-[#00D2EE]">cell KPIs</span>
                    <span className="text-white"> on High-Frequency Stations.</span>
                  </span>
                </h1>

                {/* Subtitle / Description (Exactly 1 Line on desktop) */}
                <p className="mt-5 sm:mt-6 max-w-none text-[13.5px] sm:text-[14.5px] md:text-[15.5px] lg:text-[16px] leading-[24px] sm:leading-[26px] text-white/80 whitespace-normal lg:whitespace-nowrap">
                  How a Tier 2 component plant used XP Robotics and Simpel AI to reduce repetitive manual work and lift operational KPIs.
                </p>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-14 lg:mt-16 flex flex-col items-center cursor-pointer group select-none"
                  title="Scroll to continue"
                >
                  <svg
                    width="20"
                    height="126"
                    viewBox="0 0 20 126"
                    fill="none"
                    className="text-white/75 transition-transform duration-300 group-hover:translate-y-1.5"
                  >
                    <line
                      x1="10"
                      y1="0"
                      x2="10"
                      y2="114"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeDasharray="4 4"
                    />
                    <path
                      d="M5 110L10 118L15 110"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            Case at a glance Section (matching Screenshot)
            Background: rgba(245, 250, 254, 1)
            4-Column Horizontal Card Row
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Heading row with gradient text and horizontal divider */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* Case Summary 4-Column Card Grid Container */}
            <div className="w-full rounded-[8px] sm:rounded-[10px] border border-[#EAF2FA] bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                {CASE_SUMMARY.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className={`flex items-start gap-4 p-6 sm:p-7 min-h-[175px] transition-colors duration-200 hover:bg-[#F8FAFC]/50 ${
                        idx < 3 ? "lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 2 ? "md:max-lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx % 2 === 0 ? "md:max-lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 3 ? "max-md:border-b border-[#EAF2FA]" : ""
                      }`}
                    >
                      {/* Icon Circle Badge */}
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[2]" />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1.5 pt-0.5">
                        <h3 className="text-[16px] font-bold text-[#0F172A] leading-tight">
                          {item.title}
                          {item.subtitle && (
                            <span className="block text-[13.5px] font-semibold text-[#0F172A] mt-0.5">
                              {item.subtitle}
                            </span>
                          )}
                        </h3>
                        <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: THE SITUATION (Repetitive manual work was limiting performance.)
            Background Details from Figma Inspect:
            Rectangle 85: Width 952px | Height 530px | Rotation -180 deg
            Linear Gradient: rgba(4, 20, 33, 1) -> rgba(16, 82, 135, 0)
            Background Graphic: the-situation-bg.jpg
           ========================================================================= */}
        <section
          id="the-situation"
          className="relative w-full overflow-hidden flex items-center min-h-[500px] lg:min-h-[530px]"
          style={{ backgroundColor: "rgba(4, 20, 33, 1)" }}
        >
          {/* Background Graphic provided by user */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/the-situation-bg.jpg"
              alt="The Situation - Worker on factory assembly line"
              className="w-full h-full object-cover object-right select-none"
            />

            {/* Rectangle 85: Linear Gradient overlay from rgba(4, 20, 33, 1) to rgba(16, 82, 135, 0) */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(4, 20, 33, 1) 0%, rgba(4, 20, 33, 0.98) 38%, rgba(4, 20, 33, 0.85) 54%, rgba(16, 82, 135, 0.25) 72%, rgba(16, 82, 135, 0) 90%)",
              }}
            />

            {/* Mobile dark overlay for guaranteed text legibility */}
            <div className="absolute inset-0 bg-[rgba(4,20,33,0.85)] sm:hidden pointer-events-none" />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px] py-16 sm:py-20 lg:py-24">
            <div className="max-w-[620px] flex flex-col items-start text-left">
              {/* Title row with horizontal divider line */}
              <div className="flex items-center mb-1">
                <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Situation
                </h2>
                <div className="hidden sm:block w-[140px] md:w-[180px] lg:w-[220px] h-[1px] bg-white/20 ml-6" />
              </div>

              {/* Subheading */}
              <h3 className="mt-3.5 sm:mt-4 text-[20px] sm:text-[22px] lg:text-[24px] font-bold text-white tracking-tight leading-snug">
                Repetitive manual work was limiting performance.
              </h3>

              {/* Narrative description */}
              <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[25px] sm:leading-[27px] text-[#CBD5E1]">
                Operators on three high-frequency stations spent entire shifts
                lifting parts into fixtures, loading and unloading machines,
                fastening components and tending equipment between cycles. The work
                was repetitive and physically demanding, and cell efficiency sat
                4-6 points below target because stations were limited by
                operator pace and recovery time, not by machine capacity. Handling
                variation was contributing to rework and customer PPM. Labor
                cost per part and on-time delivery were under pressure.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: WHAT WAS DONE (A collaborative robot cell with full visibility.)
            Left: Full-bleed robotic cell image (/images/insights/what-was-done.jpg)
            Right: Narrative content with cyan/blue gradient header & horizontal line
           ========================================================================= */}
        <section
          id="what-was-done"
          className="w-full relative flex flex-col lg:flex-row items-stretch overflow-hidden bg-white"
        >
          {/* Left Column: Image flush to the left edge, fills 50% width on lg screens */}
          <div className="w-full lg:w-1/2 min-h-[360px] sm:min-h-[440px] lg:min-h-[500px] xl:min-h-[520px] relative shrink-0 bg-slate-900 overflow-hidden">
            <img
              src="/images/insights/what-was-done.jpg"
              alt="XP Robotics collaborative cell in automotive plant"
              className="w-full h-full object-cover object-center block select-none"
            />
          </div>

          {/* Right Column: Content area matching exact screenshot layout and typography */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-14 xl:px-16 py-12 sm:py-16 lg:py-20">
            <div className="max-w-[560px] w-full mx-auto lg:mx-0 flex flex-col text-left">
              {/* Heading row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  What was done
                </h2>
                <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Subheading */}
              <h3 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bold text-[#334155] tracking-tight leading-snug">
                A collaborative robot cell with full visibility.
              </h3>

              {/* Content Paragraphs (unbolded as in screenshot) */}
              <div className="mt-5 sm:mt-6 flex flex-col gap-4 text-[13px] sm:text-[13.5px] lg:text-[14px] leading-[23px] sm:leading-[24px] text-[#526071]">
                <p>
                  One pilot station- the highest- frequency loading and fastening cell- was chosen
                </p>

                <p>
                  One pilot station — the highest-frequency loading and fastening cell — was
                  chosen. XP Robotics took over the physical work. A collaborative cell with
                  robot hands and arms performed the lifting, positioning, loading and
                  fastening that operators had been doing by hand. The existing fixtures
                  stayed. Live production was not stopped. A formal safety risk assessment was
                  completed before the cell ran production parts
                </p>

                <p>
                  Simpel AI connected the cell to the rest of the plant. The Plant Head could
                  see live status, cycle time, output and utilization on a single dashboard.
                  Work-order and quality data flowed into the existing ERP and MES. When the cell
                  needed attention, a clear signal reached maintenance. Quality could pull
                  process records for audits. IT had a defined integration path and role-based
                  access, so the cell did not become an orphan system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: THE INSIGHTS (Higher efficiency. Lower manual handling. Measurable results.)
            Background: automobile-insights-bg.jpg (futuristic dashboard with robotic analytics)
            Overlay: automobile-insights-overlay.png (dark blue fade on left to semi-transparent)
           ========================================================================= */}
        <section
          id="the-insights"
          className="relative w-full overflow-hidden flex items-center min-h-[440px] sm:min-h-[480px] lg:min-h-[520px] xl:min-h-[560px] bg-[#040C18]"
        >
          {/* Background Image: Futuristic Dashboard & Automotive Casting */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/automobile-insights-bg.jpg"
              alt="The Insights - Analytics dashboards and robotic casting"
              className="w-full h-full object-cover object-center select-none"
            />

            {/* Gradient Overlay PNG provided by user (Image 1) */}
            <img
              src="/images/insights/automobile-insights-overlay.png"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
            />

            {/* Mobile dark scrim for legibility on very narrow screens */}
            <div className="absolute inset-0 bg-[#061120]/75 md:hidden pointer-events-none" />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px] py-16 sm:py-20 lg:py-24">
            <div className="max-w-[540px] flex flex-col items-start text-left">
              {/* Title row with horizontal divider line */}
              <div className="flex items-center mb-1">
                <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Insights
                </h2>
                <div className="hidden sm:block w-[120px] md:w-[130px] h-[1px] bg-white/20 ml-6" />
              </div>

              {/* Subheading (2 lines) */}
              <h3 className="mt-3.5 sm:mt-4 text-[19px] sm:text-[22px] lg:text-[24px] font-bold text-white tracking-tight leading-snug">
                Higher efficiency. Lower manual handling.
                <br />
                Measurable results.
              </h3>

              {/* Narrative description */}
              <p className="mt-4 sm:mt-5 text-[13px] sm:text-[14px] leading-[22px] sm:leading-[24px] text-slate-300">
                The pilot showed clear improvements on the existing fixtures,
                <br className="hidden sm:inline" /> without stopping customer orders.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: OUR APPROACH (A focused and practical path to results.)
            Background: /images/insights/automobile-approach-bg.png (mountain sunrise with light trails)
            Heading: "Our Approach" + horizontal divider line
            Subheading: "A focused and practical path to results."
            4 Stepped Columns (01, 02, 03, 04) with ascending trajectory curve & icon badges
           ========================================================================= */}
        <section
          id="our-approach"
          className="relative w-full overflow-hidden bg-white min-h-[780px] lg:min-h-[850px] pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20"
        >
          {/* Background Mountain Landscape with Sunrise & Light Trails */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/automobile-approach-bg.png"
              alt="Our Approach - Mountain landscape with light trails"
              className="w-full h-full object-cover object-bottom select-none"
            />
          </div>

          {/* Main Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px]">
            {/* 1. Header with brand gradient & horizontal line */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Our Approach
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading */}
            <div className="max-w-[800px] mb-8 sm:mb-10">
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F172A] tracking-tight">
                A focused and practical path to results.
              </h3>
            </div>

            {/* 3. The 4 Stepped Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 relative">
              {/* Step 01 */}
              <div className="flex flex-col pr-0 lg:pr-8 lg:pt-28 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#A855F7] leading-none">
                  01
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Choose the right station
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Select one highest-frequency loading and fastening cell as a pilot
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 02 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 lg:pt-20 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#3B82F6] leading-none">
                  02
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Automate the physical work
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  XP robotics handles lifting, positioning, loading and fastening with existing fixtures retained.
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 03 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 lg:pt-10 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#00D2EE] leading-none">
                  03
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Connect to your systems
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Simpel AI integrates with ERP and MES, provides live visibility and enables alerts and audit records.
                </p>

                {/* Vertical Divider for desktop */}
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 04 */}
              <div className="flex flex-col pl-0 lg:pl-8 lg:pt-0">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#F97316] leading-none">
                  04
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Measure and expand
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Track KPI outcomes and roll out to similar stations based on proven results.
                </p>
              </div>
            </div>

            {/* 4. Ascending Trajectory Curve & Step Badges Container */}
            <div className="relative w-full mt-10 sm:mt-14 lg:mt-16 h-[200px] sm:h-[230px] lg:h-[260px]">
              {/* SVG Trajectory Path connecting 01 -> 02 -> 03 -> 04 to Summit */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 1200 240"
                fill="none"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="autoApproachTrajectoryGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="30%" stopColor="#3B82F6" />
                    <stop offset="62%" stopColor="#00D2EE" />
                    <stop offset="100%" stopColor="#F97316" />
                  </linearGradient>
                </defs>

                {/* Subtle Glow behind curve */}
                <path
                  d="M 40,215 C 180,210 320,202 460,196 C 600,190 670,182 740,172 C 830,160 930,135 1020,112 C 1070,95 1115,70 1148,55"
                  stroke="url(#autoApproachTrajectoryGradient)"
                  strokeWidth="6"
                  strokeOpacity="0.25"
                  strokeLinecap="round"
                />

                {/* Main Curved Path terminating seamlessly into the arrowhead base notch */}
                <path
                  d="M 40,215 C 180,210 320,202 460,196 C 600,190 670,182 740,172 C 830,160 930,135 1020,112 C 1070,95 1115,70 1148,55"
                  stroke="url(#autoApproachTrajectoryGradient)"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />

                {/* Sleek Aerodynamic Arrowhead pointing at 53deg towards the mountain summit */}
                <path
                  d="M 1160,39 L 1138,48 L 1148,55 L 1154,62 Z"
                  fill="#F97316"
                />

                {/* Vertical connecting dash lines from badges to dots */}
                <line
                  x1="180"
                  y1="168"
                  x2="180"
                  y2="208"
                  stroke="#A855F7"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="460"
                  y1="158"
                  x2="460"
                  y2="196"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="740"
                  y1="130"
                  x2="740"
                  y2="172"
                  stroke="#00D2EE"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />
                <line
                  x1="1020"
                  y1="68"
                  x2="1020"
                  y2="112"
                  stroke="#F97316"
                  strokeWidth="1.5"
                  strokeDasharray="2 3"
                />

                {/* Dots on trajectory curve */}
                <circle cx="180" cy="208" r="5" fill="#A855F7" />
                <circle cx="460" cy="196" r="5" fill="#3B82F6" />
                <circle cx="740" cy="172" r="5" fill="#00D2EE" />
                <circle cx="1020" cy="112" r="5" fill="#F97316" />
              </svg>

              {/* 4 Icon Badges Floating on trajectory */}
              {/* Badge 1 (Purple - Target) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "15%", top: "72%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(168,85,247,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/auto-approach-1.png"
                    alt="01 - Choose the right station"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 2 (Blue - Robotic Arm) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "38.3%", top: "67%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(59,130,246,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/auto-approach-2.png"
                    alt="02 - Automate the physical work"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 3 (Cyan - Systems / Monitor) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "61.7%", top: "54%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(0,210,238,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/auto-approach-3.png"
                    alt="03 - Connect to your systems"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Badge 4 (Orange - Growth / Chart) */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: "85%", top: "27%" }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(249,115,22,0.25)] hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/insights/auto-approach-4.png"
                    alt="04 - Measure and expand"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: THE RESULTS AFTER 9-12 MONTHS (Clear and measurable impact.)
            Background: rgba(245, 250, 254, 1)
            Left: KPI Results Table + Footnote
            Right: Testimonial Quote Card (Plant Head)
           ========================================================================= */}
        <section
          id="the-results"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] w-full px-6 lg:px-[70px]">
            {/* 1. Heading row with brand gradient text and horizontal line */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                The Results After 9-12 Months
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading */}
            <h3 className="text-[20px] sm:text-[22px] lg:text-[24px] font-bold text-[#0F172A] tracking-tight mb-8 sm:mb-10">
              Clear and measurable impact.
            </h3>

            {/* 3. Two-Column Layout (KPI Table + Quote Card) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
              {/* Left Column: KPI Results Table */}
              <div className="lg:col-span-7 flex flex-col">
                <div className="w-full rounded-[10px] sm:rounded-[12px] overflow-hidden border border-[#E2E8F0] bg-white shadow-sm">
                  {/* Table Header */}
                  <div className="flex items-center justify-between px-6 py-4 bg-[#0A1A2F] text-white">
                    <span className="text-[12px] sm:text-[13px] font-bold tracking-wider uppercase">
                      KPI
                    </span>
                    <span className="text-[12px] sm:text-[13px] font-bold tracking-wider uppercase">
                      RESULT
                    </span>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-[#EAF2FA]">
                    {/* Row 1 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        Manual handling time on pilot station
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#0F172A] text-right">
                        -65% to -72%
                      </span>
                    </div>

                    {/* Row 2 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        Cell efficiency
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#0F172A] text-right">
                        +3 to +5 points
                      </span>
                    </div>

                    {/* Row 3 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        Cycle time on pilot part family
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#0F172A] text-right">
                        Improved (existing fixtures retained)
                      </span>
                    </div>

                    {/* Row 4 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        Handling-related defects
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#0F172A] text-right">
                        -40% to -65%
                      </span>
                    </div>

                    {/* Row 5 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        Payback on pilot investment
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#0F172A] text-right">
                        11–14 months
                      </span>
                    </div>

                    {/* Row 6 */}
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[#F8FAFC]/60 transition-colors">
                      <span className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A]">
                        First-year net savings
                      </span>
                      <span className="text-[13px] sm:text-[14px] font-bold text-[#10B981] text-right">
                        $280k – $750k
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footnote */}
                <p className="mt-4 text-[11.5px] sm:text-[12px] italic text-[#64748B] leading-relaxed">
                  Based on pilot and early expansion at a mid-sized Automobile Tier 2 component supplier. Results vary with part mix and shift pattern.
                </p>
              </div>

              {/* Right Column: Quote Card */}
              <div className="lg:col-span-5 flex flex-col">
                <div className="w-full rounded-[14px] sm:rounded-[16px] border border-[#E2E8F0] bg-white p-7 sm:p-9 lg:p-10 shadow-[0_4px_24px_rgba(15,23,42,0.03)] flex flex-col justify-between min-h-[380px]">
                  {/* Quote Icon & Text */}
                  <div>
                    {/* Cyan Double Quotation Marks */}
                    <svg
                      className="w-10 h-8 sm:w-12 sm:h-9 text-[#00D2EE] mb-6 select-none"
                      viewBox="0 0 32 24"
                      fill="currentColor"
                    >
                      <path d="M0 14C0 6.268 5.373 0 12 0v5C8.134 5 5 8.134 5 12h7v12H0V14zm20 0C20 6.268 25.373 0 32 0v5c-3.866 0-7 3.134-7 7h7v12H20V14z" />
                    </svg>

                    {/* Quote Paragraph */}
                    <p className="text-[15px] sm:text-[16px] lg:text-[16.5px] italic text-[#1E293B] leading-[26px] sm:leading-[28px]">
                      “We did not need a full plant transformation. We needed one station that stopped constraining our cycle time and our cost per part. The robot cell did the work. Simpel AI let us see it and run it inside our existing systems. That was enough to say yes to the next stations.”
                    </p>
                  </div>

                  {/* Attribution */}
                  <div className="mt-8 pt-6 border-t border-[#F1F5F9]">
                    <p className="text-[13.5px] sm:text-[14px] font-bold text-[#0F172A]">
                      — Plant Head,
                    </p>
                    <p className="text-[12.5px] sm:text-[13px] text-[#64748B] mt-0.5">
                      Automobile Tier 2 component supplier
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: HOW IT HELPED EACH DECISION MAKER (A single pilot. Multiple outcomes.)
            Background: rgba(255, 255, 255, 1)
            6 Cards Grid (3 columns on desktop, 2 rows)
           ========================================================================= */}
        <section
          id="decision-makers"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] w-full px-6 lg:px-[70px]">
            {/* 1. Header with gradient text and horizontal line */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it helped each decision maker
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading */}
            <h3 className="text-[20px] sm:text-[22px] lg:text-[24px] font-bold text-[#0F172A] tracking-tight mb-8 sm:mb-12">
              A single pilot. Multiple outcomes.
            </h3>

            {/* 3. 6 Cards Grid (3 columns on lg, 2 on md, 1 on mobile) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
              {DECISION_MAKERS.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className={`rounded-[14px] sm:rounded-[16px] border ${item.borderColor} bg-white p-6 sm:p-7 transition-all duration-200 hover:shadow-md flex flex-col justify-start`}
                  >
                    {/* Icon Badge */}
                    <div
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${item.badgeBg} ${item.badgeColor} mb-5`}
                    >
                      <Icon className="w-4 h-4 sm:w-4.5 sm:h-4.5 stroke-[2.2]" />
                    </div>

                    {/* Title */}
                    <h4 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] leading-snug">
                      {item.title}
                    </h4>

                    {/* Description */}
                    <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: CTA (Ready to improve your operations?)
            Background: automobile-cta-bg.png / automobile-cta-bg.jpg
            Left: Dark navy area, headline (1 line), subheading, description, Get in Touch button, email
            Right: Assembly line car chassis (exact match to screenshot)
           ========================================================================= */}
        <section
          id="ready-to-improve"
          className="relative w-full overflow-hidden flex items-center min-h-[280px] sm:min-h-[300px] lg:min-h-[320px] bg-[#020814]"
        >
          {/* Background Image: Automotive Manufacturing Assembly Line */}
          <div className="absolute inset-0 pointer-events-none select-none">
            <img
              src="/images/insights/automobile-cta-bg.jpg"
              alt="Automotive Manufacturing Assembly Line"
              className="w-full h-full object-cover object-[right_center] select-none"
            />

            {/* Mobile dark scrim for guaranteed text contrast on small screens */}
            <div className="absolute inset-0 bg-[#020814]/80 sm:hidden pointer-events-none" />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px] py-10 sm:py-12 lg:py-14">
            <div className="max-w-[720px] flex flex-col items-start text-left">
              {/* Title (Single line) */}
              <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] xl:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent whitespace-normal sm:whitespace-nowrap">
                Ready to improve your operations?
              </h2>

              {/* Subheading */}
              <h3 className="mt-3 sm:mt-3.5 text-[18px] sm:text-[20px] lg:text-[22px] font-bold text-white tracking-tight leading-snug">
                Discuss a pilot for your Tier 2 plant.
              </h3>

              {/* Description */}
              <p className="mt-3 sm:mt-4 max-w-[560px] text-[13px] sm:text-[13.5px] lg:text-[14px] leading-[22px] sm:leading-[23px] text-slate-300">
                See how Simpel AI and XP Robotics can help you reduce manual work, improve cell efficiency and lower cost per part.
              </p>

              {/* Action Buttons */}
              <div className="mt-6 sm:mt-7 flex flex-wrap items-center gap-6 sm:gap-8">
                {/* White Pill Button */}
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 sm:px-7 py-2.5 sm:py-3 rounded-full bg-white text-[#0F172A] font-semibold text-[13.5px] sm:text-[14px] hover:bg-slate-100 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <span>Get in Touch</span>
                  <span className="text-[15px] leading-none">→</span>
                </Link>

                {/* Email Link */}
                <a
                  href="mailto:contact@simpel.ai"
                  className="inline-flex items-center gap-2.5 text-[13.5px] sm:text-[14px] font-medium text-white/90 hover:text-white transition-colors group"
                >
                  <Mail className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-[#00D2EE] group-hover:scale-110 transition-transform" />
                  <span>contact@simpel.ai</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default AutomobileCaseStudy;
